import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, KostenGehandicaptKind} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type KostenGehandicaptKindFormGroup = FormGroup<{
  naam: FormControl<string | null>;
  geboortedatumKind: FormControl<Date | null>;
  dagenVerzorging: FormControl<number | null>;
  dagenGereisd: FormControl<number | null>;
  afstandEnkeleReis: FormControl<number | null>;
  vergoeding: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-pga-weekenduitgaven-gehandicapte-kinderen',
  templateUrl: './pga-weekenduitgaven-gehandicapte-kinderen.component.html',
  styleUrls: ['./pga-weekenduitgaven-gehandicapte-kinderen.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    CommonModule
  ]
})
export class PgaWeekenduitgavenGehandicapteKinderenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  private year: any;

  public formGroup = new FormGroup({
    kostenGehandicaptKind: new FormArray<KostenGehandicaptKindFormGroup>([])
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.year = data.year;
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaWeekendUitgavenGehandicapteKinderen?.kostenGehandicaptKind || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.kostenGehandicaptKind.getRawValue().map((w) => ({
        naam: w.naam,
        geboortedatumKind: w.geboortedatumKind,
        dagenVerzorging: w.dagenVerzorging,
        dagenGereisd: w.dagenGereisd,
        afstandEnkeleReis: w.afstandEnkeleReis,
        vergoeding: w.vergoeding
      }) as KostenGehandicaptKind);

    this.changeSubscriptions.add(
      this.kostenGehandicaptKind.valueChanges
        .pipe(
          debounceTime(500),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          tap(() => this.saveForm()),
          switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe()
    );
  }

  get kostenGehandicaptKind(): FormArray<KostenGehandicaptKindFormGroup> {
    return this.formGroup.controls.kostenGehandicaptKind;
  }

  addRij(): void {
    this.kostenGehandicaptKind.push(this.createFormGroup()
    );
  }

  verwijderRij(index: number): void {
    this.kostenGehandicaptKind.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({ emitEvent: false });
    this.saveForm();
  }

  createFormGroup(kosten?: KostenGehandicaptKind): KostenGehandicaptKindFormGroup {
    return new FormGroup({
      naam: new FormControl<string | null>(kosten?.naam ?? null),
      geboortedatumKind: new FormControl<Date | null>(kosten?.geboortedatumKind ?? null),
      dagenVerzorging: new FormControl<number | null>(kosten?.dagenVerzorging ?? null),
      dagenGereisd: new FormControl<number | null>(kosten?.dagenGereisd ?? null),
      afstandEnkeleReis: new FormControl<number | null>(kosten?.afstandEnkeleReis ?? null),
      vergoeding: new FormControl<number | null>(kosten?.vergoeding ?? null)
    }) as KostenGehandicaptKindFormGroup;
  }

  private updateForm(data: KostenGehandicaptKind[]): void {
    const lijst = data || [];
    const formArray = this.kostenGehandicaptKind;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, { emitEvent: false });
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], { emitEvent: false });
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    const kostenGehandicaptKind = this.formGroup
      .getRawValue()
      .kostenGehandicaptKind.filter((restant) =>
        restant.afstandEnkeleReis || restant.dagenGereisd || restant.vergoeding ||
        restant.dagenVerzorging || restant.naam || restant.geboortedatumKind)
      .map(
        (verplichtingen) =>
          ({
            afstandEnkeleReis: verplichtingen.afstandEnkeleReis,
            dagenGereisd: verplichtingen.dagenGereisd,
            vergoeding: verplichtingen.vergoeding,
            dagenVerzorging: verplichtingen.dagenVerzorging,
            naam: verplichtingen.naam,
            geboortedatumKind: verplichtingen.geboortedatumKind
          }) as KostenGehandicaptKind
      );

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte.persoonsgebondenAftrek,
            pgaWeekendUitgavenGehandicapteKinderen: {
              ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaWeekendUitgavenGehandicapteKinderen,
              kostenGehandicaptKind: kostenGehandicaptKind
            }
          }
        }
      } as IncomeTaxEntity;
    });
  }

  hasKindJongerDan21(): boolean {
    return this.kostenGehandicaptKind.controls.some((control) => {
      const geboortedatum = control.controls.geboortedatumKind.value;

      if (!geboortedatum) {
        return false;
      }

      return new Date(geboortedatum).getFullYear() > this.year - 21;
    });
  }
}
